import React, { useState } from 'react';
import { MapPin, ChevronDown, X } from 'lucide-react';
import { CategoryIcon, getManufacturerColor } from './shared';
import { BACKEND } from '@/utils/apiConfig';
import i18n from 'i18next';

export const ProjectModal = ({ project, onClose, theme, txt, projectCategories }) => {
  const [galleryIdx, setGalleryIdx] = useState(0);
  if (!project) return null;

  const getCategoryForProject = (slug) => projectCategories.find(c => c.slug === slug);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={() => { onClose(); }} data-testid="project-modal">
      <div className="bg-white sm:rounded-2xl w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl rounded-t-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="relative h-48 sm:h-80 md:h-96 overflow-hidden rounded-t-2xl">
          <img
            src={(() => { const imgs = project.images || []; const img = imgs[galleryIdx] || imgs[0] || '/images/default-project.jpg'; return img.startsWith('/') ? `${BACKEND}${img}` : img; })()}
            alt={project.title} className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          {project.images && project.images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setGalleryIdx(i => i > 0 ? i - 1 : project.images.length - 1); }} className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"><ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 rotate-90" /></button>
              <button onClick={(e) => { e.stopPropagation(); setGalleryIdx(i => i < project.images.length - 1 ? i + 1 : 0); }} className="absolute right-12 sm:right-14 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"><ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 -rotate-90" /></button>
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">{galleryIdx + 1} / {project.images.length}</span>
            </>
          )}
          <button onClick={() => onClose()} className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors" data-testid="close-modal-button"><X className="w-4 h-4 sm:w-5 sm:h-5" /></button>
        </div>
        {project.images && project.images.length > 1 && (
          <div className="flex gap-1 p-2 bg-slate-50 overflow-x-auto">
            {project.images.map((img, idx) => (
              <button key={idx} onClick={() => setGalleryIdx(idx)} className={`flex-shrink-0 w-12 h-9 sm:w-16 sm:h-12 rounded overflow-hidden border-2 transition-all ${idx === galleryIdx ? 'border-green-500 ring-1 ring-green-300' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                <img src={img.startsWith('/') ? `${BACKEND}${img}` : img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <div className="p-6 md:p-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: `${theme.font_heading}, sans-serif` }}>{project.title}</h2>
          <div className="flex flex-wrap gap-2 sm:gap-3 mb-6">
            {(() => { const cat = getCategoryForProject(project.category); return cat ? (
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs sm:text-sm font-medium text-white" style={{ backgroundColor: cat.color }}>
                <CategoryIcon iconName={cat.icon} color="#fff" size={14} />
                {(i18n.language || '').startsWith('en') && cat.name_en ? cat.name_en : cat.name_cs}
              </div>
            ) : null; })()}
            <div className="flex items-center text-slate-600 text-sm"><MapPin className="w-4 h-4 mr-1.5" style={{ color: theme.primary_color }} />{project.location}</div>
            {project.capacity_kwp && (<div className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: `${theme.primary_color}15`, color: theme.primary_color }}>{project.capacity_kwp} kWp</div>)}
            <div className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium">{project.installation_date}</div>
          </div>
          <p className="text-slate-600 mb-6 leading-relaxed" style={{ fontFamily: `${theme.font_body}, sans-serif` }}>{project.description}</p>
          {project.features.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: `${theme.font_heading}, sans-serif` }}>{txt('projects', 'features')}</h3>
              <ul className="space-y-2">
                {project.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: theme.primary_color }} />
                    <span className="text-slate-600" style={{ fontFamily: `${theme.font_body}, sans-serif` }}>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
